import { useTranslation } from "react-i18next";
import type { FitReason, Signals } from "../core/signals";
import { Why } from "../ui";
import { ageText, describeCondition, noteText, signalLabel, valueText, type SignalDef } from "./describe";

/**
 * Beside a bin row whose signal fit is not zero (I8): "fits now" or "not now", with a "why?" that shows each
 * condition, whether it holds, and the signal values behind it with their note and age.
 */
export function FitMarker({
  fit,
  reasons,
  signals,
  definitions,
  nowMs,
}: {
  fit: number;
  reasons: FitReason[];
  signals: Signals;
  definitions: Record<string, SignalDef>;
  nowMs: number;
}) {
  const { t } = useTranslation();
  if (fit === 0) return null;
  const rows: [string, string][] = [];
  for (const r of reasons) {
    const holds =
      r.holds === true
        ? t("signals.holds")
        : r.holds === false
          ? t("signals.holdsNot")
          : t("signals.unknown");
    rows.push([t(`signals.source.${r.source}`), `${describeCondition(r.when, definitions, t)} — ${holds}`]);
    for (const name of r.signals) {
      const rec = signals[name];
      const parts = rec
        ? [valueText(rec.value, t, definitions[name]?.unit), noteText(name, rec, t), ageText(rec, nowMs, t)]
        : [t("signals.unknown")];
      rows.push([signalLabel(name, definitions[name], t), parts.filter(Boolean).join(" · ")]);
    }
  }
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[11.5px] text-neutral-500"
      data-testid="fit-marker"
      data-fit={fit}
    >
      {fit > 0 ? t("signals.fitsNow") : t("signals.notNow")}
      <Why title={t("signals.whyTitle")} rows={rows} formula={t("signals.formula")} />
    </span>
  );
}
